import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect } from "react";
import { getFragmentData, graphql } from "../gql";
import { OnboardingLiveCreationIntentFragment } from "./OnboardingLiveFragments.ts";
import {
  OnboardingLiveCreationIntentStatus,
  OnboardingLiveRecurringSlotsInput,
  OnboardingLiveSlotInput,
} from "../gql/graphql.ts";
import { GetOnboardingLiveCreationIntentsQuery } from "./useOnboardingLiveCreationIntents.ts";
import { GetOnboardingLivesQuery } from "./useOnboardingLives.ts";

const GetIntentQuery = graphql(`
  query getOnboardingLiveCreationIntent($intentId: String!) {
    getOnboardingLiveCreationIntent(intentId: $intentId) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const ChangeUserGroupMutation = graphql(`
  mutation changeOnboardingLiveCreationIntentUserGroup(
    $intentId: String!
    $userGroupId: String!
  ) {
    changeOnboardingLiveCreationIntentUserGroup(
      intentId: $intentId
      userGroupId: $userGroupId
    ) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const AddSlotsMutation = graphql(`
  mutation addOnboardingLiveCreationIntentSlots(
    $intentId: String!
    $slots: [OnboardingLiveSlotInput!]!
  ) {
    addOnboardingLiveCreationIntentSlots(intentId: $intentId, slots: $slots) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const AddRecurringSlotsMutation = graphql(`
  mutation addOnboardingLiveCreationIntentRecurringSlots(
    $intentId: String!
    $recurrence: OnboardingLiveRecurringSlotsInput!
  ) {
    addOnboardingLiveCreationIntentRecurringSlots(
      intentId: $intentId
      recurrence: $recurrence
    ) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const UpdateSlotMutation = graphql(`
  mutation updateOnboardingLiveCreationIntentSlot(
    $intentId: String!
    $slotId: String!
    $slot: OnboardingLiveSlotInput!
  ) {
    updateOnboardingLiveCreationIntentSlot(
      intentId: $intentId
      slotId: $slotId
      slot: $slot
    ) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const RemoveSlotsMutation = graphql(`
  mutation removeOnboardingLiveCreationIntentSlots(
    $intentId: String!
    $slotIds: [String!]!
  ) {
    removeOnboardingLiveCreationIntentSlots(
      intentId: $intentId
      slotIds: $slotIds
    ) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const CheckIntentMutation = graphql(`
  mutation checkOnboardingLiveCreationIntent($intentId: String!) {
    checkOnboardingLiveCreationIntent(intentId: $intentId) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const ConfirmIntentMutation = graphql(`
  mutation confirmOnboardingLiveCreationIntent($intentId: String!) {
    confirmOnboardingLiveCreationIntent(intentId: $intentId) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const CancelIntentMutation = graphql(`
  mutation cancelOnboardingLiveCreationIntent($intentId: String!) {
    cancelOnboardingLiveCreationIntent(intentId: $intentId) {
      ...OnboardingLiveCreationIntentItem
    }
  }
`);

const creationPollIntervalInMs = 15 * 1000;

export function useOnboardingLiveCreationIntent(intentId: string) {
  const { data, loading, startPolling, stopPolling, refetch } = useQuery(
    GetIntentQuery,
    { variables: { intentId }, fetchPolicy: "cache-and-network" },
  );
  const intent = getFragmentData(
    OnboardingLiveCreationIntentFragment,
    data?.getOnboardingLiveCreationIntent,
  );
  const isCreatingOnboardingLives =
    intent?.status === OnboardingLiveCreationIntentStatus.Confirmed;
  useEffect(() => {
    if (!isCreatingOnboardingLives) return;
    startPolling(creationPollIntervalInMs);
    return () => stopPolling();
  }, [isCreatingOnboardingLives, startPolling, stopPolling]);

  const refetchQueries = [GetOnboardingLiveCreationIntentsQuery];
  const [changeUserGroupMutation] = useMutation(ChangeUserGroupMutation);
  const [addSlotsMutation] = useMutation(AddSlotsMutation);
  const [addRecurringSlotsMutation] = useMutation(AddRecurringSlotsMutation);
  const [updateSlotMutation] = useMutation(UpdateSlotMutation);
  const [removeSlotsMutation] = useMutation(RemoveSlotsMutation);
  const [checkMutation, { loading: checking }] =
    useMutation(CheckIntentMutation);
  const [confirmMutation, { loading: confirming }] = useMutation(
    ConfirmIntentMutation,
    { refetchQueries: [...refetchQueries, GetOnboardingLivesQuery] },
  );
  const [cancelMutation, { loading: cancelling }] = useMutation(
    CancelIntentMutation,
    { refetchQueries },
  );

  const changeUserGroup = useCallback(
    async (userGroupId: string) => {
      await changeUserGroupMutation({ variables: { intentId, userGroupId } });
    },
    [changeUserGroupMutation, intentId],
  );
  const addSlots = useCallback(
    async (slots: OnboardingLiveSlotInput[]) => {
      await addSlotsMutation({ variables: { intentId, slots } });
    },
    [addSlotsMutation, intentId],
  );
  const addRecurringSlots = useCallback(
    async (recurrence: OnboardingLiveRecurringSlotsInput) => {
      await addRecurringSlotsMutation({ variables: { intentId, recurrence } });
    },
    [addRecurringSlotsMutation, intentId],
  );
  const updateSlot = useCallback(
    async (slotId: string, slot: OnboardingLiveSlotInput) => {
      await updateSlotMutation({ variables: { intentId, slotId, slot } });
    },
    [updateSlotMutation, intentId],
  );
  const removeSlots = useCallback(
    async (slotIds: string[]) => {
      await removeSlotsMutation({ variables: { intentId, slotIds } });
    },
    [removeSlotsMutation, intentId],
  );
  const check = useCallback(async () => {
    await checkMutation({ variables: { intentId } });
  }, [checkMutation, intentId]);
  const confirm = useCallback(async () => {
    await confirmMutation({ variables: { intentId } });
  }, [confirmMutation, intentId]);
  const cancel = useCallback(async () => {
    await cancelMutation({ variables: { intentId } });
  }, [cancelMutation, intentId]);

  return {
    intent,
    loading,
    refetch,
    changeUserGroup,
    addSlots,
    addRecurringSlots,
    updateSlot,
    removeSlots,
    check,
    confirm,
    cancel,
    checking,
    confirming,
    cancelling,
  };
}
